'use client';

import { Slider } from'@/components/ui/slider';
import { PREFERENCE_DESCRIPTIONS } from'@/lib/types/preferences';
import { ReactNode } from'react';
import { cn } from'@/lib/utils/cn';
import { AppErrorBoundary } from'@/components/common/AppErrorBoundary';

interface SliderLevel {
 value: number;
 label: string;
 icon?: string;
}

interface SliderWithDescriptionProps {
 value: number;
 onChange: (value: number) => void;
 title: string;
 description: string;
 levels: SliderLevel[];
 icon?: ReactNode;
 descriptionKey: keyof typeof PREFERENCE_DESCRIPTIONS;
}

export default function SliderWithDescription({
 value,
 onChange,
 title,
 description,
 levels,
 icon,
 descriptionKey
}: SliderWithDescriptionProps) {
 // FIX: Ensure value is always a valid number to prevent Radix UI conditional hook issues
 // This prevents React #185 when preferences load and value changes from undefined to number
 const safeValue = typeof value ==='number' && !isNaN(value) ? Math.max(1, Math.min(5, value)) : 3;
 
 const currentDescription = PREFERENCE_DESCRIPTIONS[descriptionKey]?.[safeValue as keyof typeof PREFERENCE_DESCRIPTIONS[typeof descriptionKey]];
 
 const badgeClassName = cn(
'rounded-full px-3 py-1 text-sm font-semibold tracking-wide text-[var(--color-text-on-accent)] shadow-lg transition-colors backdrop-blur-sm accent-gradient-primary'
 );

 return (
 <AppErrorBoundary source="slider-with-description">
 {/* Monitoring boundary ensures ErrorBoundary coverage from'@/components/common/ErrorMonitoringV2' */}
 <div
 className={cn(
'bg-theme-bg-secondary space-y-4 rounded-2xl p-5 shadow-xl transition-all backdrop-blur-xl border border-theme-border-primary'
 )}
 >
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-2">
 {icon}
 <h4 className={cn('font-semibold text-theme-text-primary')}>{title}</h4>
 </div>
 <span className={badgeClassName}>
 {safeValue}/5
 </span>
 </div>
 
 <p className={cn('text-sm text-theme-text-secondary')}>{description}</p>
 
 <Slider
 value={[safeValue]}
 onValueChange={(vals) => onChange(vals[0] ?? 1)}
 min={1}
 max={5}
 step={1}
 className="w-full"
 />
 
 <div className="flex justify-between text-xs px-1">
   {levels.map((level) => (
     <span
       key={level.value}
       className={cn(
         safeValue === level.value ?'font-semibold accent-text-primary' :'text-theme-text-secondary'
       )}
     >
       {level.label}
     </span>
   ))}
 </div>
 
 <div
 className={cn(
'bg-theme-bg-primary rounded-xl p-3 border transition-colors',
'accent-border-primary/40'
 )}
 >
 <p className={cn('text-sm italic leading-relaxed text-theme-text-primary')}>{currentDescription}</p>
 </div>
 </div>
 </AppErrorBoundary>
 );
}
